import { useState, useEffect } from 'react';
import { Save, Check } from 'lucide-react';

interface NotesSectionProps {
  reportId: string;
  notes: string | null;
  readOnly: boolean;
  onNotesUpdate: (notes: string) => Promise<void>;
}

export const NotesSection: React.FC<NotesSectionProps> = ({
  notes,
  readOnly,
  onNotesUpdate,
}) => {
  const [localNotes, setLocalNotes] = useState(notes ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(true);

  // Sync local state when notes prop changes
  useEffect(() => {
    setLocalNotes(notes ?? '');
    setIsSaved(true);
  }, [notes]);

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalNotes(e.target.value);
    setIsSaved(false);
  };

  const handleSave = async () => {
    if (isSaving || readOnly) return;

    setIsSaving(true);
    try {
      await onNotesUpdate(localNotes);
      setIsSaved(true);
    } catch (error) {
      console.error('Failed to save notes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBlur = () => {
    // Auto-save on blur if notes have changed
    if (!isSaved && !readOnly) {
      handleSave();
    }
  };

  const characterCount = localNotes.length;
  const maxCharacters = 5000;

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">
              {characterCount} / {maxCharacters} characters
            </span>
            {!readOnly && (
              <>
                {isSaved ? (
                  <div className="flex items-center gap-1.5 text-sm text-green-600">
                    <Check className="h-4 w-4" />
                    <span>Saved</span>
                  </div>
                ) : (
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="bg-blue-600 text-white px-3 py-1.5 rounded-lg font-medium text-sm hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="h-3.5 w-3.5 inline mr-1.5" />
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <textarea
          value={localNotes}
          onChange={handleNotesChange}
          onBlur={handleBlur}
          placeholder={
            readOnly
              ? 'No notes available'
              : 'Add notes for this report (e.g., observations for the client)...'
          }
          rows={5}
          maxLength={maxCharacters}
          disabled={readOnly}
          className={`w-full px-4 py-3 border border-gray-200 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            readOnly ? 'bg-gray-50 cursor-not-allowed text-gray-600' : 'bg-white'
          }`}
        />
        {readOnly && (
          <p className="text-xs text-gray-500 italic">
            Notes are read-only in Creative view
          </p>
        )}
      </div>
    </div>
  );
};
